import { create } from 'zustand';
import type { AdminUser } from './adminTypes';
import {
  readPersistedAccessToken,
  readPersistedRefreshToken,
  writePersistedAccessToken,
  writePersistedRefreshToken,
} from './tokenStorage';

export type AdminAuthStatus = 'idle' | 'loading' | 'authed' | 'disabled' | 'anon';

interface AdminAuthState {
  /** Memory-only access token. NEVER persisted to localStorage. */
  accessToken: string | null;
  /** Memory-only refresh token (backend returns it in the login body). NEVER persisted. */
  refreshToken: string | null;
  admin: AdminUser | null;
  status: AdminAuthStatus;
  setSession: (accessToken: string, admin: AdminUser, refreshToken?: string | null) => void;
  setStatus: (status: AdminAuthStatus) => void;
  clearSession: () => void;
}

export const useAuthStore = create<AdminAuthState>()((set) => ({
  // Hydrated from storage so reloads / new tabs start with a token.
  // Missing/expired tokens fail closed downstream (401 → refresh → login).
  accessToken: readPersistedAccessToken(),
  refreshToken: readPersistedRefreshToken(),
  admin: null,
  status: 'idle',
  setSession: (accessToken, admin, refreshToken = null) => {
    writePersistedAccessToken(accessToken);
    writePersistedRefreshToken(refreshToken);
    set({
      accessToken,
      refreshToken,
      admin,
      status: admin.status === 'disabled' ? 'disabled' : 'authed',
    });
  },
  setStatus: (status) => set({ status }),
  // Memory-only clear: persisted tokens survive so the next boot can
  // recover via refresh (spurious 401s don't nuke the session). Explicit
  // logout clears storage via clearPersistedTokens().
  clearSession: () => set({ accessToken: null, refreshToken: null, admin: null, status: 'anon' }),
}));

/** `Authorization: Bearer <token>` value, or null when logged out. */
export function getAuthHeader(): string | null {
  const token = useAuthStore.getState().accessToken;
  return token ? `Bearer ${token}` : null;
}
